/* eslint-disable object-curly-newline, object-property-newline, no-nested-ternary */

import { detectCoinHybrid } from './coinHybrid';
import { gradedTone } from './toneGraded';

const CONTEXT_KEYWORDS = ['during', 'when', 'while', 'meeting', 'project', 'semester', 'week', 'phase'];
const OBSERVATION_KEYWORDS = ['contributed', 'completed', 'organized', 'led', 'communicated', 'explained', 'presented', 'researched', 'wrote', 'coded', 'designed', 'built', 'helped', 'participated', 'debugged', 'analyzed', 'delivered', 'prepared', 'implemented', 'met deadlines', 'shared ideas', 'gave feedback'];
const IMPACT_KEYWORDS = ['because of', 'as a result', 'which helped', 'which allowed', 'enabled', 'allowed', 'boosted', 'enhanced', 'valuable to the team', 'asset to the team', 'helped the team'];
const NEXT_STEP_KEYWORDS = ['could', 'should', 'might', 'consider', 'next time', 'in the future', 'going forward', 'improve', 'work on', 'focus on', 'keep doing', 'continue'];

const TASK_KEYWORDS = ['project', 'report', 'presentation', 'assignment', 'code', 'coding', 'circuit', 'analysis', 'python', 'matlab', 'excel', 'github', 'completed', 'built', 'wrote', 'coded', 'designed'];
const PROCESS_KEYWORDS = ['strategy', 'approach', 'method', 'process', 'workflow', 'debugging', 'figured out', 'researched', 'tested', 'iterated', 'communicated', 'coordinated', 'collaborated', 'brainstormed', 'split tasks'];
const SELF_REG_KEYWORDS = ['learned', 'adapted', 'adjusted', 'improved', 'growth', 'took feedback', 'asked for feedback', 'self-directed', 'independently', 'proactive', 'time management'];
const SELF_KEYWORDS = ['friendly', 'kind', 'nice', 'reliable', 'responsible', 'dedicated', 'hard-working', 'hardworking', 'smart', 'talented', 'great teammate', 'great team member'];

const POSITIVE_KEYWORDS = ['good', 'great', 'excellent', 'amazing', 'helpful', 'valuable', 'appreciate', 'well done', 'strong'];
const GROWTH_KEYWORDS = ['however', 'but', 'could improve', 'should try', 'work on', 'consider', 'next time', 'going forward', 'needs to'];
const GROWTH_FALSE_POSITIVES = [/\bother than that\b/, /\bapart from that\b/, /\boverall\b/];
const NEGATIVE_PATTERNS = [
  ['accusatory', /\byou\s+(?:never\b|always\s+(?:fail|forget|miss|slack))/],
  ['personal_attack', /\b(?:lazy|incompetent|useless|irresponsible|terrible|awful|worst)\b/],
  ['demanding', /\byou\s+(?:must|need\s+to|have\s+to|better)\b/],
];
const CONSTRUCTIVE_PATTERNS = [
  /\b(?:maybe|perhaps|possibly|might\s+want\s+to|you\s+could|consider)\b/,
  /\b(?:have\s+you\s+(?:considered|tried|thought)|could\s+you|would\s+you|what\s+if)\b/,
];
// v6.3 (2026-05-23): problematic wrap-up phrasings -- needs coaching, not block.
// Mirrors server/src/services/feedbackFacilitator3d.scoring.js; see comment
// there for rationale and the Pass 1 prompt HOLISTIC JUDGMENT clause.
const PROBLEMATIC_WRAP_UP_PATTERNS = [
  /\bpotential\s+but\b/,
  /\b(?:isn(?:'|')?t|aren(?:'|')?t)\s+(?:really\s+)?(?:for\s+you|your\s+thing|cut\s+out\s+for)\b/,
  /\b(?:didn(?:'|')?t\s+seem|weren(?:'|')?t\s+(?:really\s+)?)\s*(?:invested|engaged|interested|committed)\b/,
  /\bmaybe\s+(?:it(?:'|')?s\s+)?not\s+(?:for\s+you|your\s+thing|your\s+passion|your\s+strength)\b/,
  /\bokay\s+to\s+admit\b/,
  /\bcontributed\s+in\s+your\s+own\s+way\b/,
];

const GROWTH_MINDSET_KEYWORDS = ['effort', 'hard work', 'dedication', 'persistence', 'learned', 'developed', 'improved', 'progress', 'the way you', 'how you approached'];
const FIXED_MINDSET_KEYWORDS = ['smart', 'brilliant', 'genius', 'natural', 'talented', 'gifted', 'you are a', "you're a", 'naturally'];
const SDT_GROUPS = [
  ['chose to', 'took the initiative', 'volunteered', 'independently', 'you could consider'],
  ['skilled', 'knowledgeable', 'capable', 'competent', 'your skills', 'your knowledge', "you've improved"],
  ['team', 'group', 'together', 'collaborated', 'helped us', 'supported', 'willing to help'],
];

const SUBSTANCE_MATRIX = {
  '0,self': 0, '0,task': 0, '0,process': 0, '0,self_regulation': 0, '0,unclassifiable': 0.5,
  '1,self': 1, '1,task': 1, '1,process': 1, '1,self_regulation': 1.5, '1,unclassifiable': 1,
  '2,self': 1.5, '2,task': 2, '2,process': 2, '2,self_regulation': 2, '2,unclassifiable': 1.5,
  '3,self': 2.5, '3,task': 2.5, '3,process': 2.5, '3,self_regulation': 3, '3,unclassifiable': 2.5,
  '4,self': 3, '4,task': 3, '4,process': 3.5, '4,self_regulation': 3.5, '4,unclassifiable': 3.5,
  '5,self': 3.5, '5,task': 4, '5,process': 4.5, '5,self_regulation': 5, '5,unclassifiable': 4,
};

const STRUCTURE_MATRIX = {
  '0,0': 0, '0,1': 1, '0,2': 2,
  '1,0': 1, '1,1': 1.5, '1,2': 2.5,
  '2,0': 2, '2,1': 3, '2,2': 4,
  '3,0': 3, '3,1': 4, '3,2': 5,
};

export const EMPTY_ASSESSMENT = Object.freeze({
  score: 0,
  label: 'minimal',
  routing: 'rewrite_required',
  suggestions: [],
  coaching: '',
  total_score: 0,
  routing_label: 'rewrite_required',
  classification: 'minimal',
  dimensions: { substance: 0, structure: 0, tone: 0 },
  safety: { level: 2, mandatory_rephrase: false },
  evidence: {
    coin_elements: {},
    hattie_level: 'unclassifiable',
    adjustments: { total_adjustment: 0 },
    substance_diagnosis: 'too_brief',
    structure_diagnosis: 'no_structure',
    tone_diagnosis: 'balanced',
  },
  details: { substance: {}, structure: {}, tone: {} },
  meta: { empty: true, wordCount: 0 },
});

function escapeKeyword(keyword) {
  return keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function normalizeFeedbackText(value) {
  if (typeof value !== 'string') {
    return '';
  }
  return value.replace(/\s+/g, ' ').trim();
}

export function isMeaningfulFeedbackText(value) {
  const normalized = normalizeFeedbackText(value);
  return normalized.length > 0 && normalized !== 'N/A';
}

function countKeywordHits(textLower, keywords) {
  return keywords.filter((keyword) => {
    if (keyword.includes(' ')) {
      return textLower.includes(keyword);
    }
    return new RegExp(`\\b${escapeKeyword(keyword)}\\b`).test(textLower);
  }).length;
}

function pyRound1(value) {
  const shifted = value * 10;
  const floored = Math.floor(shifted);
  const fraction = shifted - floored;
  if (Math.abs(fraction - 0.5) < 1e-9) {
    return (floored % 2 === 0 ? floored : floored + 1) / 10;
  }
  return Math.round(shifted) / 10;
}

function scoreLabel(score) {
  if (score >= 8.5) return 'exceptional';
  if (score >= 6.5) return 'strong';
  if (score >= 4.5) return 'adequate';
  if (score >= 2.5) return 'developing';
  return 'minimal';
}

function routeLabel(classification, safetyLevel) {
  const routeMap = {
    exceptional: 'ready_to_send',
    strong: 'ready_to_send',
    adequate: 'needs_minor_revision',
    developing: 'needs_structural_revision',
    minimal: 'rewrite_required',
  };

  let route = routeMap[classification];
  if (safetyLevel === 0) {
    route = 'rewrite_required';
  } else if (safetyLevel === 1 && route === 'ready_to_send') {
    route = 'needs_minor_revision';
  }
  return route;
}

function classifyHattie(textLower, wordCount) {
  const task = countKeywordHits(textLower, TASK_KEYWORDS);
  const process = countKeywordHits(textLower, PROCESS_KEYWORDS);
  const selfReg = countKeywordHits(textLower, SELF_REG_KEYWORDS);
  const self = countKeywordHits(textLower, SELF_KEYWORDS);
  if (wordCount <= 3) return 'unclassifiable';
  if (selfReg >= 1) return 'self_regulation';
  if (process >= 2 || (process >= 1 && task === 0)) return 'process';
  if (task >= 1) return 'task';
  if (process >= 1) return 'process';
  if (self >= 1 || wordCount <= 15) return 'self';
  return 'unclassifiable';
}

function scoreSubstance(textLower, wordCount) {
  const hattie = classifyHattie(textLower, wordCount);
  const wcTier = wordCount <= 3 ? 0 : wordCount <= 7 ? 1 : wordCount <= 15 ? 2 : wordCount <= 30 ? 3 : wordCount <= 49 ? 4 : 5;
  const hasHighSpec = ['python', 'matlab', 'excel', 'github', 'code', 'report', 'presentation', 'circuit'].some((keyword) => textLower.includes(keyword));
  const effectiveTier = hasHighSpec && wcTier >= 2 && wcTier < 5 ? Math.min(wcTier + 1, 5) : wcTier;
  return [SUBSTANCE_MATRIX[`${effectiveTier},${hattie}`] || 0, hattie, { word_count: wordCount, has_high_spec: hasHighSpec }];
}

function hasButFalsePositive(textLower) {
  return /\b(?:quiet|shy|reserved|introverted|soft-spoken)\s+but\s+(?:valuable|great|good|helpful|effective|important|dedicated|hard-working|reliable)\b/.test(textLower)
    || /\bbut\s+(?:overall|other\s+than\s+that|besides\s+that|apart\s+from)\b/.test(textLower);
}

function scoreStructure(textLower, wordCount, originalText) {
  const legacyCoin = {
    context: countKeywordHits(textLower, CONTEXT_KEYWORDS) > 0,
    observation: countKeywordHits(textLower, OBSERVATION_KEYWORDS) > 0,
    impact: countKeywordHits(textLower, IMPACT_KEYWORDS) > 0,
    next_steps: countKeywordHits(textLower, NEXT_STEP_KEYWORDS) > 0 && !hasButFalsePositive(textLower),
  };
  // programmatic_v2 Spike B: union the legacy keyword detection with
  // the compositional clause-level patterns + precision filter on
  // short-comment observation FPs. Verified to lift structure r from
  // 0.666 to 0.672 on the 17,599-comment derived-3D Claude corpus.
  // STRUCTURE_MATRIX kept at legacy values -- the fitted matrix was a
  // calibration band-aid that didn't generalize to the broader corpus.
  const coin = detectCoinHybrid(originalText || textLower, legacyCoin);
  const actionability = countKeywordHits(textLower, ['try', 'consider', 'could', 'should', 'next time', 'going forward']) > 0
    ? (countKeywordHits(textLower, ['be more', 'communicate', 'respond', 'practice', 'learn', 'focus', 'work on']) > 0 ? 2 : 1)
    : 0;
  const coinCount = Object.values(coin).filter(Boolean).length;
  const coinTier = coinCount >= 4 ? 3 : coinCount >= 2 ? 2 : coinCount >= 1 ? 1 : 0;
  let structure = STRUCTURE_MATRIX[`${coinTier},${actionability}`] || 0;
  if (wordCount < 8 && structure > 2) {
    structure = 2;
  } else if (wordCount < 16 && structure > 3) {
    structure = 3;
  }
  return [structure, { coin_elements: coin, coin_count: coinCount, coin_tier: coinTier, actionability }];
}

function scoreTone(textLower, wordCount) {
  const positive = countKeywordHits(textLower, POSITIVE_KEYWORDS);
  let growth = countKeywordHits(textLower, GROWTH_KEYWORDS);
  if (GROWTH_FALSE_POSITIVES.some((pattern) => pattern.test(textLower))) {
    growth = 0;
  }
  const negativeMatches = NEGATIVE_PATTERNS.filter(([, pattern]) => pattern.test(textLower)).map(([name]) => name);
  const constructive = CONSTRUCTIVE_PATTERNS.some((pattern) => pattern.test(textLower));
  const wrapUpMatches = PROBLEMATIC_WRAP_UP_PATTERNS.filter((pattern) => pattern.test(textLower)).length;
  const hasPositive = positive > 0;
  const hasGrowth = growth > 0 || (textLower.includes('but') && wordCount >= 16);
  const hasNegative = negativeMatches.length > 0;
  const hasProblematicWrapUp = wrapUpMatches > 0;
  const evidence = {
    positive: hasPositive,
    growth: hasGrowth,
    negative_tone: negativeMatches,
    constructive_tone: constructive,
    has_negative_tone: hasNegative,
    problematic_wrap_up: hasProblematicWrapUp,
  };
  if (hasNegative && !constructive && !hasPositive && !hasGrowth) return [0, evidence];
  if (hasPositive && hasGrowth) return [hasProblematicWrapUp ? 0.5 : 1, evidence];
  if (constructive && hasPositive) return [hasProblematicWrapUp ? 0.5 : 1, evidence];
  return [0.5, evidence];
}

function computeAdjustments(textLower, wordCount, substance) {
  let total = 0;
  const growthHits = countKeywordHits(textLower, GROWTH_MINDSET_KEYWORDS);
  const fixedHits = countKeywordHits(textLower, FIXED_MINDSET_KEYWORDS);
  const adjustments = {
    growth_mindset_bonus: 0,
    fixed_mindset_penalty: 0,
    sdt_support_bonus: 0,
    verbosity_penalty: 0,
  };
  if (growthHits > fixedHits && growthHits > 0) {
    adjustments.growth_mindset_bonus = 0.25;
    total += 0.25;
  }
  if (fixedHits > growthHits * 2 && fixedHits >= 2) {
    adjustments.fixed_mindset_penalty = -0.5;
    total -= 0.5;
  }
  const supportedNeeds = SDT_GROUPS.filter((group) => countKeywordHits(textLower, group) > 0).length;
  if (supportedNeeds >= 2) {
    adjustments.sdt_support_bonus = 0.25;
    total += 0.25;
  }
  if (wordCount >= 50 && substance <= 2.5) {
    adjustments.verbosity_penalty = -0.5;
    total -= 0.5;
  }
  adjustments.total_adjustment = total;
  return adjustments;
}

function buildSuggestions(evidence) {
  const suggestions = [];
  if (evidence.substance_diagnosis === 'too_brief') {
    suggestions.push('Add a concrete example of what the person did.');
  }
  if (evidence.substance_diagnosis === 'trait_only') {
    suggestions.push('Focus on observable actions instead of personality traits.');
  }
  if (evidence.structure_diagnosis === 'observation_only') {
    suggestions.push('Explain why the action mattered to the team or project.');
  }
  if (evidence.structure_diagnosis === 'missing_next_steps' || evidence.structure_diagnosis === 'vague_suggestion') {
    suggestions.push('Add one clear next step or keep-doing suggestion.');
  }
  if (evidence.tone_diagnosis === 'critical_only') {
    suggestions.push('Balance growth feedback with at least one strength.');
  }
  if (evidence.tone_diagnosis === 'positive_only') {
    suggestions.push('Add one small area for growth to make the feedback more useful.');
  }
  return suggestions.slice(0, 3);
}

export function createEmptyAssessment(inputText = '') {
  return {
    ...EMPTY_ASSESSMENT,
    text: typeof inputText === 'string' ? inputText : '',
  };
}

export function scoreFeedbackText(inputText) {
  const normalizedText = normalizeFeedbackText(inputText);
  if (!isMeaningfulFeedbackText(normalizedText)) {
    return createEmptyAssessment(typeof inputText === 'string' ? inputText : '');
  }

  try {
    const wordCount = normalizedText.split(/\s+/).filter(Boolean).length;
    const textLower = normalizedText.toLowerCase();
    const [substance, hattieLevel, substanceDetail] = scoreSubstance(textLower, wordCount);
    const [structure, structureDetail] = scoreStructure(textLower, wordCount, normalizedText);
    const [legacyToneRaw, toneEvidence] = scoreTone(textLower, wordCount);
    // programmatic_v2 Spike G: apply extra growth + negative pattern
    // adjustments. Verified to lift tone r 0.502 -> 0.552 on the 1,000-
    // sample native-3D Claude set; +0.003 on 17k corpus.
    const tone = gradedTone(legacyToneRaw, toneEvidence, normalizedText);
    const adjustments = computeAdjustments(textLower, wordCount, substance);

    let totalScore = Math.max(1, Math.min(10, pyRound1(substance + structure + tone + adjustments.total_adjustment)));
    // v6.3: problematic_wrap_up bumps safety to 1 (cautionary). Mirrors server.
    const safetyLevel = tone === 0 ? 0 : toneEvidence.has_negative_tone || toneEvidence.problematic_wrap_up ? 1 : 2;
    const mandatoryRephrase = safetyLevel === 0;
    if (safetyLevel === 0 && totalScore > 5) {
      totalScore = 5;
    } else if (safetyLevel === 1 && totalScore > 8.5) {
      totalScore = 8.5;
    }

    let classification = scoreLabel(totalScore);
    let routingLabel = routeLabel(classification, safetyLevel);
    // Content-gated (not length-gated): only force a rewrite for a short comment when it
    // also names NO concrete observation. A short-but-specific comment ("Rebuilt the model")
    // must not be force-routed to rewrite on length alone (R6).
    if (wordCount <= 6 && !structureDetail.coin_elements.observation && routingLabel !== 'rewrite_required') {
      classification = 'minimal';
      routingLabel = 'rewrite_required';
    }

    const evidence = {
      coin_elements: structureDetail.coin_elements,
      hattie_level: hattieLevel,
      adjustments,
      substance_diagnosis: (wordCount <= 3 && !structureDetail.coin_elements.observation) ? 'too_brief' : hattieLevel === 'self' && wordCount >= 8 ? 'trait_only' : hattieLevel === 'task' && wordCount >= 16 ? 'task_level_could_deepen' : 'adequate',
      structure_diagnosis: structureDetail.coin_count === 0 ? 'no_structure' : !structureDetail.coin_elements.observation ? 'missing_observation' : !structureDetail.coin_elements.impact && !structureDetail.coin_elements.next_steps ? 'observation_only' : !structureDetail.coin_elements.next_steps && structureDetail.actionability === 0 ? 'missing_next_steps' : structureDetail.actionability === 1 ? 'vague_suggestion' : 'adequate',
      tone_diagnosis: mandatoryRephrase ? 'harsh' : toneEvidence.problematic_wrap_up ? 'problematic_wrap_up' : toneEvidence.positive && !toneEvidence.growth ? 'positive_only' : toneEvidence.growth && !toneEvidence.positive ? 'critical_only' : 'balanced',
    };

    return {
      text: normalizedText,
      score: totalScore,
      label: classification,
      routing: routingLabel,
      suggestions: buildSuggestions(evidence),
      coaching: '',
      total_score: totalScore,
      routing_label: routingLabel,
      classification,
      dimensions: { substance, structure, tone },
      safety: { level: safetyLevel, mandatory_rephrase: mandatoryRephrase },
      evidence,
      details: { substance: substanceDetail, structure: structureDetail, tone: toneEvidence },
      meta: { empty: false, wordCount },
    };
  } catch {
    return {
      ...createEmptyAssessment(typeof inputText === 'string' ? inputText : ''),
      suggestions: ['Name a specific action your teammate took, its impact, and a next step.'],
      meta: { empty: false, failed: true, wordCount: 0 },
    };
  }
}
